/**
 * TownsimProvenanceBannerComponent — simulation-session provenance strip.
 *
 * When the customer shell runs inside a TOWNSIM_ORG swapped session (the townsim
 * building-click portal), this strip keeps the exact simulated selection visibly bound
 * to the banking surface. Every rendered value is a fact from the opaque, browser-safe
 * GET /bff/townsim/session projection. Root/effective subjects, bank org/account ids,
 * provisioning data, and bearer material never cross this boundary.
 *
 * Dismissible-but-persistent: collapsing reduces the strip to a compact chip that
 * still names the selected entity — a simulation session is never rendered indistinguishable
 * from a real one. Collapse state is per-tab (sessionStorage), mirroring the
 * factor-expiry banner's dismissal convention.
 *
 * Integrated in AppComponent's banner stack, like the cosession/factor-expiry banners.
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslocoDirective, TranslocoService, provideTranslocoScope } from '@jsverse/transloco';
import {
  ApiService,
  isActiveTownsimSession,
  type TownsimSessionResponse,
  type TownsimSessionSelection,
} from '../api.service';
import { isConfiguredSimHost } from '../demo/persona-switch.model';

const COLLAPSE_KEY = 'tb-townsim-provenance-collapsed';

function readCollapsed(): boolean {
  try {
    return sessionStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

function saveCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) sessionStorage.setItem(COLLAPSE_KEY, '1');
    else sessionStorage.removeItem(COLLAPSE_KEY);
  } catch { /* private-browsing / quota — collapse just won't persist */ }
}

/** Non-empty trimmed string, else null — the wire may omit or blank any binding field. */
function fact(value: string | null | undefined): string | null {
  const v = value?.trim();
  return v ? v : null;
}

@Component({
  selector: 'tb-townsim-provenance-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective],
  providers: [provideTranslocoScope('townsimProvenance')],
  template: `
    <ng-container *transloco="let t; read: 'townsimProvenance'">
      @if (visible()) {
        <div
          class="sim-strip"
          role="status"
          [attr.aria-label]="t('stripAria')"
          data-testid="townsim-provenance-banner"
        >
          @if (!collapsed()) {
            <div class="strip-body">
              <div class="strip-line">
                <span class="strip-icon material-symbols-outlined" aria-hidden="true">science</span>
                <span class="sim-badge" data-testid="townsim-provenance-badge">{{ t('badge') }}</span>
                <!-- {{ ' ' }} nodes survive Angular's whitespace collapsing (flex drops them
                     visually, but screen readers / textContent keep the word boundary). -->
                {{ ' ' }}
                @if (entityLabel(); as entity) {
                  <strong
                    class="biz-label"
                    data-testid="townsim-provenance-business"
                    [title]="entityTitle()"
                  >{{ entity }}</strong>
                }
              </div>
              @if (hasFacts()) {
                <div class="strip-line strip-facts">
                  @if (entityKind(); as kind) {
                    <span class="fact" data-testid="townsim-provenance-kind">
                      <span class="fact-label">{{ t('entityKind') }}</span>{{ ' ' }}
                      <span class="fact-value">{{ kind }}</span>
                    </span>
                  }
                  @if (memberLabel(); as member) {
                    <span class="fact" data-testid="townsim-provenance-member" [title]="memberTitle()">
                      <span class="fact-label">{{ t('member') }}</span>{{ ' ' }}
                      <span class="fact-value">{{ member }}</span>
                    </span>
                  }
                  @if (memberRole(); as role) {
                    <span class="fact" data-testid="townsim-provenance-role">
                      <span class="fact-label">{{ t('role') }}</span>{{ ' ' }}
                      <span class="fact-value">{{ role }}</span>
                    </span>
                  }
                  @if (agentId(); as agent) {
                    <span class="fact" data-testid="townsim-provenance-agent">
                      <span class="fact-label">{{ t('agent') }}</span>{{ ' ' }}
                      <span class="fact-value amount">{{ agent }}</span>
                    </span>
                  }
                </div>
              }
            </div>
            <button
              class="strip-toggle"
              type="button"
              (click)="collapse()"
              aria-expanded="true"
              [attr.aria-label]="t('collapseAria')"
              data-testid="townsim-provenance-collapse"
            >
              <span class="material-symbols-outlined" aria-hidden="true">expand_less</span>
            </button>
          } @else {
            <button
              class="sim-chip"
              type="button"
              (click)="expand()"
              aria-expanded="false"
              [attr.aria-label]="t('expandAria')"
              data-testid="townsim-provenance-chip"
            >
              <span class="material-symbols-outlined chip-icon" aria-hidden="true">science</span>
              <span class="chip-text">{{ t('badge') }}@if (entityLabel(); as name) {&nbsp;— {{ name }}}</span>
              <span class="material-symbols-outlined chip-icon" aria-hidden="true">expand_more</span>
            </button>
          }
        </div>
      }
    </ng-container>
  `,
  styles: `
    :host { display: block; }

    .sim-strip {
      display: flex; align-items: flex-start; gap: 0.6rem;
      background: color-mix(in srgb, var(--tb-status-info, #7aa2f7) 12%, var(--tb-surface-panel, #1a1d24));
      border-bottom: 2px solid var(--tb-status-info, #7aa2f7);
      padding: 0.4rem 0.85rem;
      font-size: 0.85rem;
    }
    .strip-body {
      flex: 1; min-width: 0;
      display: flex; flex-direction: column; gap: 0.15rem;
      padding: 0.15rem 0;
    }
    .strip-line { display: flex; align-items: center; gap: 0.5rem; min-width: 0; }
    .strip-icon {
      font-size: 1.1rem; flex-shrink: 0;
      color: var(--tb-status-info, #7aa2f7);
      font-variation-settings: 'OPSZ' 20, 'wght' 400;
    }
    .sim-badge {
      flex-shrink: 0; white-space: nowrap;
      font-size: 0.72rem; font-weight: 650; letter-spacing: 0.04em; text-transform: uppercase;
      color: var(--tb-status-info, #7aa2f7);
      border: 1px solid color-mix(in srgb, var(--tb-status-info, #7aa2f7) 55%, transparent);
      border-radius: var(--tb-radius-pill, 999px);
      padding: 0.05rem 0.5rem;
    }
    .biz-label {
      min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      font-size: 0.92rem; color: var(--tb-text-primary, #e6e9ef);
    }

    .strip-facts { flex-wrap: wrap; row-gap: 0.1rem; color: var(--tb-text-muted, #9aa5b1); }
    .fact { display: inline-flex; align-items: baseline; gap: 0.3rem; min-width: 0; }
    .fact:not(:first-child)::before { content: '·'; opacity: 0.6; }
    .fact-label {
      white-space: nowrap;
      font-size: 0.72rem; font-weight: 650; letter-spacing: 0.04em; text-transform: uppercase;
    }
    .fact-value {
      max-width: 14rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      color: var(--tb-text-primary, #e6e9ef);
    }
    .fact-value.amount { font-variant-numeric: tabular-nums; }

    .strip-toggle {
      background: none; border: none; cursor: pointer; padding: 0.15rem;
      color: var(--tb-status-info, #7aa2f7);
      display: flex; align-items: center; justify-content: center;
      opacity: 0.8; flex-shrink: 0;
      min-width: var(--tb-touch, 48px); min-height: var(--tb-touch, 48px);
    }
    .strip-toggle:hover { opacity: 1; }
    .strip-toggle:focus-visible { outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 2px; }
    .strip-toggle .material-symbols-outlined { font-size: 1.1rem; }

    .sim-chip {
      display: inline-flex; align-items: center; gap: 0.35rem;
      background: none;
      border: 1px solid color-mix(in srgb, var(--tb-status-info, #7aa2f7) 55%, transparent);
      border-radius: var(--tb-radius-pill, 999px);
      color: var(--tb-status-info, #7aa2f7);
      padding: 0.2rem 0.7rem;
      font-size: 0.78rem; font-weight: 600; cursor: pointer;
      min-height: 32px; max-width: 100%;
    }
    .sim-chip .chip-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .sim-chip .chip-icon { font-size: 0.95rem; font-variation-settings: 'OPSZ' 16, 'wght' 400; }
    .sim-chip:hover { background: color-mix(in srgb, var(--tb-status-info, #7aa2f7) 12%, transparent); }
    .sim-chip:focus-visible { outline: 2px solid var(--tb-brand-accent, #4f86f7); outline-offset: 2px; }

    @media (max-width: 600px) {
      .sim-strip { font-size: 0.78rem; }
      .fact-value { max-width: 8rem; }
      .biz-label { font-size: 0.82rem; }
    }
  `,
})
export class TownsimProvenanceBannerComponent {
  private readonly api = inject(ApiService);
  private readonly transloco = inject(TranslocoService);

  private readonly session = signal<TownsimSessionResponse | null>(null);
  readonly collapsed = signal<boolean>(readCollapsed());
  /** Shell hydration key fences a same-page persona switch without exposing raw identity. */
  readonly contextKey = input('initial');
  readonly memberHandle = input('');
  readonly selectionChanged = output<{ contextKey: string; selection: TownsimSessionSelection | null }>();

  /** The strip exists only for a coherent active TownSim persona projection. */
  private readonly selection = computed(() => {
    const session = this.session();
    return session && isActiveTownsimSession(session) ? session.selection : null;
  });
  readonly visible = computed(() => this.selection() !== null);

  readonly entityLabel = computed(() => fact(this.selection()?.entityLabel));
  readonly entityKind = computed(() => fact(this.selection()?.entityKind));
  readonly memberLabel = computed(() => fact(this.selection()?.memberLabel));
  readonly memberRole = computed(() => fact(this.selection()?.memberRole));

  /** Never coerced — Number(null) === 0 would fabricate an agent id for an absent field. */
  readonly agentId = computed<number | null>(() => {
    const v = this.selection()?.simulationAgentId;
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  });

  readonly hasFacts = computed(
    () => !!(this.entityKind() || this.memberLabel() || this.memberRole() || this.agentId() !== null),
  );

  constructor() {
    // live-journeys triage #2: /bff/townsim/session is gated server-side to a configured sim
    // host (TownsimSessionController#isConfiguredSimHost) and returns a bare 403 off one. This
    // strip renders unconditionally in the app shell, so gate the fetch itself on the SAME
    // client-side host check rather than firing a guaranteed-403 request on every route.
    effect(onCleanup => {
      const contextKey = this.contextKey();
      const memberHandle = this.memberHandle();
      this.session.set(null);
      this.selectionChanged.emit({ contextKey, selection: null });
      if (typeof window === 'undefined' || !isConfiguredSimHost(window.location.hostname)) return;
      let current = true;
      const request = this.api.currentTownsimSession().subscribe({
        next: session => {
          if (!current) return;
          if (memberHandle && isActiveTownsimSession(session)
            && session.selection?.memberHandle !== memberHandle) return;
          this.session.set(session);
          this.selectionChanged.emit({ contextKey,
            selection: isActiveTownsimSession(session) ? session.selection : null });
        },
        error: () => { /* no coherent session facts; selector remains unavailable */ },
      });
      onCleanup(() => { current = false; request.unsubscribe(); });
    });
  }

  collapse(): void {
    this.collapsed.set(true);
    saveCollapsed(true);
  }

  expand(): void {
    this.collapsed.set(false);
    saveCollapsed(false);
  }

  // Tooltip builders use only the browser-safe display projection.
  entityTitle(): string {
    const agent = this.agentId();
    return this.joinTitle([
      this.entityLabel(),
      this.entityKind(),
      agent !== null ? this.t('agentTitle', { agentId: agent }) : null,
    ]);
  }

  memberTitle(): string {
    return this.joinTitle([this.memberLabel(), this.memberRole()]);
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`townsimProvenance.${key}`, params);
  }

  private joinTitle(parts: Array<string | null>): string {
    return parts.filter((p): p is string => !!p).join(' · ');
  }
}
